import {
  Controller,
  useForm,
} from 'react-hook-form';
import {
  OTPFormData,
} from '../page';
import OTPInput from '@/components/OTPInput';
import Button from '@/components/Button';
import {
  useExchangeOtpForToken,
  useGetToken,
  useSendOTPForLogin,
} from '@/hooks/useApi';
import useLoginProvider from '@/contexts/LoginContext';
import {
  LoginRequestDeliveryMethods,
  PatientLoginRequestDTO,
  SendOTPAccountLockedDTO,
  SendOTPSuccessResponseDTO,
  VerifyOTPSuccessResponseDTO,
} from '@/dtos';
import isNil from 'lodash/isNil';
import {
  useEffect,
  useMemo,
  useState,
} from 'react';
import clsx from 'clsx';
import ToolTip from '@/core/components/Tooltip';
import {
  handleTokenResponse,
} from '@/utils/auth';
import {
  toast,
} from '@/hooks/useToast';

// Default countdown for enabling resend (fallback if API doesn't provide exiryMinutes)
const DEFAULT_EXPIRY_MINUTES = 3;

export default function OTP() {
  const { control, handleSubmit, watch, formState: { isValid } } = useForm<OTPFormData>({
    defaultValues: {
      otp: '',
    },
  });
  const otp = watch('otp');
  const { mutateAsync: verifyOTP, isPending: isVerifyingOTP } = useExchangeOtpForToken();
  const { mutateAsync: resendOTP, isPending: isResendingOTP } = useSendOTPForLogin();
  const { mutateAsync: getToken, isPending: isGetToken }= useGetToken();

  const {
    loginData,
    setTokenResponse,
    setOrganizations,
    setPageState,
    setLoginRequestIdHash,
    otpResponse,
    setOtpResponse,
    authLoginOptions,
  } = useLoginProvider();

  // Calculate initial countdown in seconds from exiryMinutes
  const initialCountdownSeconds = useMemo(() => {
    const expiryMinutes = otpResponse?.expiryMinutes ?? DEFAULT_EXPIRY_MINUTES;
    return expiryMinutes * 60;
  }, [otpResponse?.expiryMinutes, otpResponse?.requestHash]);

  const maskedPhoneNumber = useMemo(() => {
    const lastFour = loginData?.phoneNumber?.slice(-4) ?? '';
    return lastFour ? `(***) ***-${lastFour}` : '';
  }, [loginData?.phoneNumber]);

  const codeSentToLabel = useMemo(() => {
    const { deliveryType, emailAddress } = otpResponse ?? {};
    const isEmailDelivery = deliveryType === LoginRequestDeliveryMethods.Email && emailAddress;
    // Mask email address will come from the backend
    return isEmailDelivery ? `your email at ${emailAddress}` : (maskedPhoneNumber ?? 'your phone');
  }, [otpResponse?.deliveryType, otpResponse?.emailAddress, maskedPhoneNumber]);

  const onVerifyOTP = async (data: OTPFormData) => {
    if(!isCodeExpired) {
      const response =  await verifyOTP({
        otpCode: data.otp,
        requestHash: otpResponse?.requestHash ?? null,
      });

      const { loginRequestIdHash, patientOrgMappings } = response as VerifyOTPSuccessResponseDTO;

      const count = patientOrgMappings?.length ?? 0;
      if (!isNil(loginRequestIdHash) && count > 0) {
        setLoginRequestIdHash(loginRequestIdHash);
        if (count === 1) {
          try {
            const only = patientOrgMappings?.[0];
            const tokenResponse = await getToken({
              loginRequestIdHash,
              patientIdHash: only?.patientHash ?? null,
              organizationIdHash: only?.organizationHash ?? null,
            });
            if (!isNil(tokenResponse?.token)) {
              // Store the full token response
              setTokenResponse(tokenResponse);
              // Store token, refreshToken, and sessionHash in local storage
              handleTokenResponse(tokenResponse);
              // Set organizations which will trigger the useEffect in LoginPage to handle org selection
              setOrganizations(patientOrgMappings ?? []);
            }
          } catch (e) {
            // Swallow token fetch errors here; page flow can handle lack of token
            // and user can retry by re-entering OTP or resending code
            console.error('Error fetching token:', e);
          }
        } else {
          // Multiple organizations - just set them and let the page handle it
          setOrganizations(patientOrgMappings ?? []);
        }
      }
    }
  };

  useEffect(() => {
    if (isValid && otp?.length === otpResponse?.otpLength) {
      onVerifyOTP({ otp });
    }
  }, [isValid, otp, otpResponse?.otpLength]);

  const [resendCountdown, setResendCountdown] = useState<number>(initialCountdownSeconds);

  // Update countdown when otpResponse changes (e.g., after resend)
  useEffect(() => {
    setResendCountdown(initialCountdownSeconds);
  }, [initialCountdownSeconds]);

  useEffect(() => {
    if (resendCountdown <= 0) return;
    const interval = setInterval(() => {
      setResendCountdown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(interval);
  }, [resendCountdown]);

  const formattedResendTime = useMemo(() => {
    const minutes = Math.floor(resendCountdown / 60);
    const seconds = resendCountdown % 60;
    const paddedSeconds = seconds < 10 ? `0${seconds}` : `${seconds}`;
    return `${minutes}:${paddedSeconds}`;
  }, [resendCountdown]);

  const handleResendCode = async () => {
    if (!isNil(loginData)) {
      try {
        const response = await resendOTP(loginData as PatientLoginRequestDTO);

        // Check if the response is a success or account locked
        if ('requestHash' in response) {
          // Success - user received new OTP
          const successResponse = response as SendOTPSuccessResponseDTO;
          setOtpResponse(successResponse);
          setResendCountdown(initialCountdownSeconds);
          const sentViaEmail = successResponse.deliveryType === LoginRequestDeliveryMethods.Email && successResponse.emailAddress;
          toast.success(sentViaEmail
            ? `A new verification code has been sent to your email at ${successResponse.emailAddress}.`
            : 'A new verification code has been sent to your phone.');
        } else {
          // Account locked
          const lockedResponse = response as SendOTPAccountLockedDTO;
          toast.error(lockedResponse.errorMessage ?? 'Account is locked. Please contact support.');
          setPageState('login');
        }
      } catch (error) {
        console.error('Error resending OTP:', error);
        toast.error('Failed to resend code. Please try again.');
      }
    }
  };

  const canShowResend = useMemo(() => {
    return resendCountdown <= 0 && !isNil(loginData);
  }, [resendCountdown, loginData]);

  const isCodeExpired = resendCountdown === 0;

  const disabledVerify = isVerifyingOTP || otp?.length !== otpResponse?.otpLength || isCodeExpired;

  return (
    <div className="flex flex-col gap-4">
      <div className="text-center mb-4 text-gray-700 text-sm md:text-base font-medium flex flex-col gap-1">
        <span>
          Enter the {otpResponse?.otpLength ?? 6}-digit code sent to{' '}
          <span className="text-gray-900 text-sm md:text-base font-semibold tracking-wide whitespace-nowrap">
            {codeSentToLabel}.
          </span>
        </span>
        <span className="text-xs md:text-sm">
          {resendCountdown > 0 ? (
            <>Code expires in <span className="text-red-500">{formattedResendTime}</span></>
          ) : (
            <span className="text-red-500">Code expired</span>
          )}
        </span>
      </div>
      <div className="flex flex-col gap-6">
        <Controller
          name="otp"
          control={control}
          rules={{
            required: 'Code is required',
            pattern: {
              value: new RegExp(`^\\d{${otpResponse?.otpLength ?? 6}}$`),
              message: `Please enter a ${otpResponse?.otpLength ?? 6}-digit Code`,
            },
          }}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <div>
              <OTPInput
                value={value}
                onChange={onChange}
                length={otpResponse?.otpLength ?? 6}
              />
              {error && (
                <p className="text-red-500 text-xs md:text-sm mt-1">{error.message}</p>
              )}
            </div>
          )}
        />
        <div>
          <div  className="flex gap-3">
            <ToolTip
              title={isCodeExpired && !isResendingOTP ? 'The code has expired. Please request a new code.' : ''}
              hide={!isCodeExpired}
              className="w-full"
            >
              <span className={clsx({ 'w-1/2 cursor-not-allowed': disabledVerify, 'w-full': !isCodeExpired })}>
                <Button
                  variant="login"
                  size="login"
                  disabled={disabledVerify}
                  onClick={handleSubmit(onVerifyOTP)}
                  className="w-full !h-12 md:!h-10 disabled:opacity-60 bg-primary hover:bg-primary/90 enabled:shadow-md enabled:hover:shadow-lg text-white font-semibold py-2 rounded-lg text-sm md:text-base tracking-wide"
                >
                  {isVerifyingOTP || isGetToken ? 'Verifying...' : 'Verify'}
                </Button>
              </span>
            </ToolTip>
            {canShowResend && (
              <Button
                variant="link"
                size="login"
                disabled={isResendingOTP}
                onClick={handleResendCode}
                className="!w-1/2 md:w-auto !h-12 md:!h-10 bg-white hover:text-black font-semibold py-2 px-6 rounded-lg border border-gray-200 shadow-sm disabled:text-gray-500 hover:bg-gray-50 hover:no-underline enabled:shadow-md enabled:hover:shadow-lg disabled:opacity-60"
              >
                {isResendingOTP ? 'Resending...' : 'Resend Code'}
              </Button>
            )}
          </div>
          {/* Resend indicators are shown in the header block above; nothing here */}
          <Button
            variant="link"
            size="login"
            onClick={() => {
              setPageState('login');
            }}
            className="w-full text-primary text-sm p-0 h-auto font-medium mt-4"
          >
            Back to Phone Number
          </Button>
          <p className="text-center text-xs text-gray-800 leading-5 max-w-md mx-auto my-4">
            Your account will be locked after <span className="text-red-600 font-semibold">{authLoginOptions?.maxAttemptBeforeLock ?? 5} failed</span> attempts.
          </p>
        </div>
      </div>
    </div>
  );
}
